import type { CSSProperties } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMe } from '../../hooks/useMe'
import { getToken } from '../../lib/auth'
import { fetchOwnerConsole } from '../../api/ownerConsole'
import KpiCard from '../../components/ownerConsole/KpiCard'
import HealthStrip from '../../components/ownerConsole/HealthStrip'
import Sparkline from '../../components/ownerConsole/Sparkline'
import SimpleTable from '../../components/ownerConsole/SimpleTable'
import OwnerConsoleSkeleton from '../../components/ownerConsole/OwnerConsoleSkeleton'
import DetailsDrawer, { type DrawerField, type DrawerTable } from '../../components/ownerConsole/DetailsDrawer'
import {
  OWNER_ACCENT,
  OWNER_BG,
  OWNER_BORDER,
  OWNER_PANEL,
  OWNER_STATUS,
  SPARKLINE_COLORS,
} from '../../components/ownerConsole/ownerTheme'
import {
  type OwnerConsoleData,
  type KpiMetric,
  type RangeKey,
  type SegmentKey,
  type TabKey,
} from './ownerConsoleTypes'

type RangeOptionValue = RangeKey | 'custom'

const RANGE_OPTIONS: { value: RangeOptionValue; label: string; disabled?: boolean }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: 'custom', label: 'Custom', disabled: true },
]

const SEGMENT_OPTIONS: { value: SegmentKey; label: string }[] = [
  { value: 'all', label: 'All platforms' },
  { value: 'web', label: 'Web' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
]

const ownerFeatureEnabled = true

type Status = 'loading' | 'ready' | 'error'
type DrawerPayload = {
  title: string
  subtitle?: string
  fields?: DrawerField[]
  table?: DrawerTable
}

export default function OwnerConsolePage() {
  const authed = Boolean(getToken())
  const { me, loading: meLoading } = useMe(authed)
  const isRoot = Boolean(me?.is_superuser)
  const isOwner = isRoot && ownerFeatureEnabled

  const [range, setRange] = useState<RangeKey>('7d')
  const [compare, setCompare] = useState(true)
  const [segment, setSegment] = useState<SegmentKey>('all')
  const [status, setStatus] = useState<Status>('loading')
  const [errorMessage, setErrorMessage] = useState('')
  const [ownerData, setOwnerData] = useState<OwnerConsoleData | null>(null)
  const [activeKpiId, setActiveKpiId] = useState('')
  const [activeTab, setActiveTab] = useState<TabKey>('growth')
  const [subsCreatorId, setSubsCreatorId] = useState('')
  const [drawerPayload, setDrawerPayload] = useState<DrawerPayload | null>(null)
  const kpiMetrics = ownerData?.kpiMetrics ?? []
  const healthItems = ownerData?.healthItems ?? []
  const tabConfigs = ownerData?.tabConfigs ?? []

  const loadOwnerData = useCallback(() => {
    if (!isOwner) return
    setStatus('loading')
    setErrorMessage('')
    fetchOwnerConsole({ range, segment, compare, creatorId: subsCreatorId || undefined })
      .then((data) => {
        setOwnerData(data)
        setStatus('ready')
      })
      .catch((err) => {
        const message =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to load Owner Console data.'
        setErrorMessage(String(message))
        setStatus('error')
      })
  }, [range, segment, compare, subsCreatorId, isOwner])

  const handleRetry = () => {
    loadOwnerData()
  }

  useEffect(() => {
    if (kpiMetrics.length === 0) {
      setActiveKpiId('')
      return
    }
    if (!kpiMetrics.some((metric) => metric.id === activeKpiId)) {
      setActiveKpiId(kpiMetrics[0].id)
    }
  }, [kpiMetrics, activeKpiId])

  useEffect(() => {
    if (!isOwner) return
    loadOwnerData()
  }, [loadOwnerData])

  useEffect(() => {
    if (status !== 'ready') setDrawerPayload(null)
  }, [status])

  const selectedKpi = useMemo(
    () => kpiMetrics.find((kpi) => kpi.id === activeKpiId),
    [kpiMetrics, activeKpiId],
  )
  const activeTabConfig = useMemo(
    () => tabConfigs.find((tab) => tab.key === activeTab) ?? tabConfigs[0],
    [tabConfigs, activeTab],
  )

  if (!authed) {
    return <div className="text-white/80">Please sign in to view the Owner Console.</div>
  }

  if (meLoading) {
    return <OwnerConsoleSkeleton />
  }

  if (!isOwner) {
    return <div className="text-white/80">Owner Console is restricted to root users.</div>
  }

  if (status === 'loading') {
    return <OwnerConsoleSkeleton />
  }

  if (status === 'error' && !ownerData) {
    return (
      <div className="rounded-2xl border p-4 text-white/80" style={{ borderColor: OWNER_BORDER }}>
        <div className="text-sm font-semibold text-white">Unable to load Owner Console data.</div>
        <div className="mt-1 text-xs text-white/60">{errorMessage}</div>
        <button
          type="button"
          onClick={handleRetry}
          className="mt-3 rounded-lg border px-3 py-2 text-xs text-white/80 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--owner-accent)]"
          style={{ borderColor: OWNER_BORDER }}
        >
          Retry
        </button>
      </div>
    )
  }

  if (!ownerData || !activeTabConfig) {
    return <OwnerConsoleSkeleton />
  }

  const highlightCard = activeTabConfig.cards[2]
  const secondaryCards = activeTabConfig.cards.slice(0, 2)
  const rangeLabel = RANGE_OPTIONS.find((option) => option.value === range)?.label ?? range
  const segmentLabel = SEGMENT_OPTIONS.find((option) => option.value === segment)?.label ?? segment

  const clearSubsFilter = () => {
    setSubsCreatorId('')
  }

  const openKpiDrawer = (metric: KpiMetric) => {
    setActiveKpiId(metric.id)
    setDrawerPayload({
      title: metric.label,
      subtitle: `${rangeLabel} - ${segmentLabel}`,
      fields: [
        { label: 'Current value', value: metric.value },
        { label: 'Trend points', value: `${metric.trend.length}` },
        { label: 'Delta', value: metric.deltaPct !== undefined ? `${metric.deltaPct.toFixed(1)}%` : 'No comparison' },
      ],
      table: { columns: ['Notes'], rows: metric.notes.map((note) => [note]) },
    })
  }

  const openRowDrawer = (row: string[], rowIndex: number) => {
    const fields: DrawerField[] = activeTabConfig.table.columns.map((col, idx) => ({
      label: col,
      value: row[idx] ?? '--',
    }))
    const table: DrawerTable = {
      columns: ['Context', 'Value'],
      rows: [
        ['Tab', activeTabConfig.label],
        ['Range', rangeLabel],
        ['Segment', segmentLabel],
        ['Row', `#${rowIndex + 1}`],
      ],
    }
    setDrawerPayload({
      title: activeTabConfig.tableTitle,
      subtitle: 'Detail view',
      fields,
      table,
    })
  }

  return (
    <div
      className="space-y-6 rounded-3xl border p-6 shadow-[0_30px_80px_rgba(0,0,0,0.45)]"
      style={
        {
          backgroundColor: OWNER_BG,
          borderColor: OWNER_BORDER,
          '--owner-accent': OWNER_ACCENT,
        } as CSSProperties
      }
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-white">Owner Console</h1>
            <span className="rounded-full border border-white/20 bg-white/5 px-2.5 py-1 text-xs text-white/70">
              Internal
            </span>
          </div>
          <p className="text-sm text-white/60">
            Internal overview of growth, revenue, and system health.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <select
            value={range}
            onChange={(event) => setRange(event.target.value as RangeKey)}
            className="rounded-lg border px-3 py-2 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--owner-accent)]"
            style={{ borderColor: OWNER_BORDER, backgroundColor: OWNER_PANEL }}
          >
            {RANGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setCompare((prev) => !prev)}
            aria-pressed={compare}
            className={`rounded-lg border px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--owner-accent)] ${
              compare ? 'text-white' : 'text-white/60'
            }`}
            style={{ borderColor: compare ? OWNER_ACCENT : OWNER_BORDER }}
          >
            {compare ? 'vs previous' : 'compare off'}
          </button>
          <select
            value={segment}
            onChange={(event) => setSegment(event.target.value as SegmentKey)}
            className="rounded-lg border px-3 py-2 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--owner-accent)]"
            style={{ borderColor: OWNER_BORDER, backgroundColor: OWNER_PANEL }}
          >
            {SEGMENT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {status === 'error' && (
        <div className="rounded-2xl border p-4" style={{ borderColor: OWNER_BORDER, backgroundColor: OWNER_PANEL }}>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-sm font-semibold text-white">Unable to load Owner Console data.</div>
              <div className="mt-1 text-xs text-white/60">
                {errorMessage || 'Please retry to fetch the latest metrics.'}
              </div>
            </div>
            <button
              type="button"
              onClick={handleRetry}
              className="rounded-lg border px-3 py-2 text-xs text-white/80 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--owner-accent)]"
              style={{ borderColor: OWNER_BORDER }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpiMetrics.map((metric) => (
          <KpiCard
            key={metric.id}
            label={metric.label}
            value={metric.value}
            deltaPct={metric.deltaPct}
            active={metric.id === activeKpiId}
            onClick={() => openKpiDrawer(metric)}
          />
        ))}
      </div>

      <div className="rounded-2xl border p-5" style={{ borderColor: OWNER_BORDER, backgroundColor: OWNER_PANEL }}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/50">Drilldown</div>
            <div className="mt-2 text-lg font-semibold text-white">{selectedKpi?.label ?? 'Select a KPI'}</div>
            <div className="mt-1 text-sm text-white/60">Latest value: {selectedKpi?.value ?? '--'}</div>
          </div>
          {selectedKpi && (
            <div className="w-full max-w-xs">
              <Sparkline series={selectedKpi.trend} height={72} />
            </div>
          )}
        </div>
        {selectedKpi && (
          <ul className="mt-4 space-y-2 text-sm text-white/70">
            {selectedKpi.notes.map((note) => (
              <li key={note} className="border-l border-white/10 pl-3">
                {note}
              </li>
            ))}
          </ul>
        )}
      </div>

      <HealthStrip items={healthItems} />

	      <div className="rounded-2xl border p-1" style={{ borderColor: OWNER_BORDER, backgroundColor: OWNER_PANEL }}>
	        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Owner console sections">
	          {tabConfigs.map((tab) => (
	            <button
	              key={tab.key}
	              type="button"
	              onClick={() => setActiveTab(tab.key)}
	              role="tab"
	              aria-selected={activeTab === tab.key}
	              aria-controls={`owner-tabpanel-${tab.key}`}
	              className={`rounded-xl px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--owner-accent)] ${
	                activeTab === tab.key ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'
	              }`}
	            >
	              {tab.label}
	            </button>
	          ))}
	        </div>
	      </div>

	      {activeTab === 'subscriptions' && (
	        <div className="flex flex-col gap-2 rounded-2xl border p-4 text-sm text-white/75 md:flex-row md:items-center md:justify-between" style={{ borderColor: OWNER_BORDER, backgroundColor: OWNER_PANEL }}>
	          <div className="flex items-center gap-2">
	            <div className="text-xs uppercase tracking-[0.2em] text-white/45">Filter</div>
	            {subsCreatorId ? (
	              <div className="rounded-full border px-2.5 py-1 text-xs text-white/75" style={{ borderColor: OWNER_BORDER }}>
	                CreatorId: {subsCreatorId}
	              </div>
	            ) : (
	              <div className="text-xs text-white/55">Click a creator in the leaderboard to drill down.</div>
	            )}
	          </div>
	          <div className="flex items-center gap-2">
	            <button
	              type="button"
	              onClick={() => loadOwnerData()}
	              className="rounded-lg border px-3 py-2 text-xs text-white/80 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--owner-accent)]"
	              style={{ borderColor: OWNER_BORDER }}
	            >
	              Refresh
	            </button>
	            <button
	              type="button"
	              onClick={clearSubsFilter}
	              disabled={!subsCreatorId}
	              className="rounded-lg border px-3 py-2 text-xs text-white/80 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--owner-accent)] disabled:opacity-50"
	              style={{ borderColor: OWNER_BORDER }}
	            >
	              Clear
	            </button>
	          </div>
	        </div>
	      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" role="tabpanel" id={`owner-tabpanel-${activeTab}`}>
        <div className="rounded-2xl border p-4 lg:col-span-2" style={{ borderColor: OWNER_BORDER, backgroundColor: OWNER_PANEL }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="text-sm text-white/70">{activeTabConfig.chartTitle}</div>
              <div className="mt-1 text-xs text-white/45">Last {range}</div>
            </div>
            {highlightCard && (
              <div className="text-right">
                <div className="text-xs text-white/50">{highlightCard.title}</div>
                <div className="text-sm text-white">{highlightCard.value}</div>
              </div>
            )}
          </div>
          <div className="mt-4">
            <Sparkline series={activeTabConfig.chartSeries} height={160} />
            {activeTabConfig.chartLegend && (
              <div className="mt-3 flex gap-4 text-xs text-white/50">
                {activeTabConfig.chartLegend.map((label, index) => (
                  <div key={label} className="flex items-center gap-2">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: SPARKLINE_COLORS[index % SPARKLINE_COLORS.length] }}
                    />
                    {label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="grid gap-4">
          {secondaryCards.map((card) => {
            const isPositive = (card.deltaPct ?? 0) >= 0
            return (
              <div
                key={card.title}
                className="rounded-2xl border p-4"
                style={{ borderColor: OWNER_BORDER, backgroundColor: OWNER_PANEL }}
              >
                <div className="text-xs uppercase tracking-[0.2em] text-white/50">{card.title}</div>
                <div className="mt-3 text-xl font-semibold text-white">{card.value}</div>
                {card.subtext && <div className="mt-1 text-xs text-white/50">{card.subtext}</div>}
                {card.deltaPct !== undefined && (
                  <div className="mt-3 text-xs" style={{ color: isPositive ? OWNER_STATUS.ok : OWNER_STATUS.bad }}>
                    {isPositive ? '+' : ''}
                    {card.deltaPct.toFixed(1)}% vs previous
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {activeTabConfig.leaderboard && (
        <SimpleTable
          title={activeTabConfig.leaderboardTitle ?? 'Top creators'}
          columns={activeTabConfig.leaderboard.columns}
          rows={activeTabConfig.leaderboard.rows}
          actionLabel="Top creators"
          actionDisabled
          onRowClick={
            activeTab === 'subscriptions'
              ? (row) => {
                  const creatorId = row[row.length - 1]
                  if (!creatorId) return
                  setSubsCreatorId(String(creatorId))
                }
              : undefined
          }
        />
      )}

      {activeTabConfig.daily && (
        <SimpleTable
          title={activeTabConfig.dailyTitle ?? 'Daily rollup'}
          columns={activeTabConfig.daily.columns}
          rows={activeTabConfig.daily.rows}
          actionLabel="Daily"
          actionDisabled
        />
      )}

      {activeTabConfig.table.rows.length === 0 ? (
        <div className="rounded-2xl border p-4" style={{ borderColor: OWNER_BORDER, backgroundColor: OWNER_PANEL }}>
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-white">{activeTabConfig.tableTitle}</div>
            <button
              type="button"
              disabled
              className="rounded-lg border px-3 py-1 text-xs text-white/60 disabled:opacity-50"
              style={{ borderColor: OWNER_BORDER }}
            >
              Export CSV
            </button>
          </div>
          <div className="mt-3 text-sm text-white/60">
            Nothing to show yet. Once new activity lands here, rows will appear with the latest details.
          </div>
        </div>
      ) : (
        <SimpleTable
          title={activeTabConfig.tableTitle}
          columns={activeTabConfig.table.columns}
          rows={activeTabConfig.table.rows}
          onRowClick={openRowDrawer}
        />
      )}

      <DetailsDrawer
        open={Boolean(drawerPayload)}
        title={drawerPayload?.title ?? ''}
        subtitle={drawerPayload?.subtitle}
        fields={drawerPayload?.fields}
        table={drawerPayload?.table}
        onClose={() => setDrawerPayload(null)}
      />
    </div>
  )
}
